import * as React from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  Pencil,
  Trash2,
  Globe,
  Database,
  Cloud,
  CheckCircle,
  XCircle,
  ExternalLink,
} from 'lucide-react';
import { api, TenantDetail, ApiError } from '@/lib/api';

export default function TenantDetailPage() {
  const params = useParams();
  const navigate = useNavigate();
  const slug = params.slug as string;

  const [tenant, setTenant] = React.useState<TenantDetail | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [isDeleting, setIsDeleting] = React.useState(false);

  React.useEffect(() => {
    const loadTenant = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await api.getTenant(slug);
        setTenant(data);
      } catch (err) {
        const apiError = err as ApiError;
        setError(apiError.message || 'Erro ao carregar plataforma');
      } finally {
        setIsLoading(false);
      }
    };

    loadTenant();
  }, [slug]);

  const handleDelete = async () => {
    if (!confirm('Tem certeza que deseja excluir esta plataforma? Esta acao nao pode ser desfeita.')) {
      return;
    }

    try {
      setIsDeleting(true);
      await api.deleteTenant(slug);
      navigate('/clients');
    } catch (err) {
      const apiError = err as ApiError;
      alert(apiError.message || 'Erro ao excluir plataforma');
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-[#0057FF]" />
          <p className="text-gray-500">Carregando plataforma...</p>
        </div>
      </div>
    );
  }

  if (error || !tenant) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link
            to="/clients"
            className="p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">Plataforma nao encontrada</h1>
        </div>
        <div className="bg-red-50 rounded-2xl border border-red-100 p-6">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-red-100 flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-red-500" />
            </div>
            <div>
              <h3 className="font-semibold text-red-800">Erro</h3>
              <p className="text-red-600 text-sm">{error || 'Plataforma nao encontrada'}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/clients"
            className="p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <span className="text-white font-bold text-xl">
                {tenant.platformName.charAt(0)}
              </span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{tenant.platformName}</h1>
              <div className="flex items-center gap-3 mt-1">
                <code className="text-sm text-gray-500">{tenant.slug}.nozcomunidades.com.br</code>
                <a
                  href={`https://${tenant.slug}.nozcomunidades.com.br`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#0057FF] hover:text-[#0057FF]/80 transition-colors"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to={`/tenants/${slug}/edit`}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <Pencil className="h-4 w-4" />
            Editar
          </Link>
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-50 border border-red-100 text-sm font-medium text-red-600 hover:bg-red-100 transition-colors disabled:opacity-50"
          >
            {isDeleting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
            Excluir
          </button>
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Basic Info */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Informacoes Gerais</h2>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-gray-500">ID</span>
              <code className="text-sm font-mono text-gray-900 bg-gray-50 px-2 py-1 rounded">{tenant.id}</code>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-gray-500">Nome da Plataforma</span>
              <span className="text-sm font-medium text-gray-900">{tenant.platformName}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-gray-500">Slug</span>
              <code className="text-sm font-mono text-gray-900 bg-gray-50 px-2 py-1 rounded">{tenant.slug}</code>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-gray-500">Status</span>
              <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                tenant.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'
              }`}>
                {tenant.status === 'active' ? 'Ativo' : 'Inativo'}
              </span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-gray-500">Plano</span>
              <span className="text-sm font-medium text-gray-900">{tenant.plan.name}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-gray-500">Criado em</span>
              <span className="text-sm text-gray-900">{new Date(tenant.createdAt).toLocaleDateString('pt-BR')}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-gray-500">Atualizado em</span>
              <span className="text-sm text-gray-900">{new Date(tenant.updatedAt).toLocaleDateString('pt-BR')}</span>
            </div>
          </div>
        </div>

        {/* Infrastructure */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Infraestrutura</h2>
          </div>
          <div className="p-6 space-y-4">
            {/* Database Schema */}
            <div className="flex items-start gap-4 p-4 rounded-xl border border-gray-100">
              <div className="h-10 w-10 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
                <Database className="h-5 w-5 text-blue-500" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-gray-900">PostgreSQL Schema</span>
                  {tenant.schemaProvisioned ? (
                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-500" />
                  )}
                </div>
                <code className="text-sm text-gray-500">tenant_{tenant.slug}</code>
              </div>
            </div>

            {/* Storage Bucket */}
            <div className="flex items-start gap-4 p-4 rounded-xl border border-gray-100">
              <div className="h-10 w-10 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0">
                <Cloud className="h-5 w-5 text-amber-500" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-gray-900">Cloudflare R2 Bucket</span>
                  {tenant.bucketProvisioned ? (
                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-500" />
                  )}
                </div>
                {tenant.bucketName && (
                  <code className="text-sm text-gray-500">{tenant.bucketName}</code>
                )}
              </div>
            </div>

            {/* DNS */}
            <div className="flex items-start gap-4 p-4 rounded-xl border border-gray-100">
              <div className="h-10 w-10 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                <Globe className="h-5 w-5 text-emerald-500" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-gray-900">DNS</span>
                  {tenant.dnsProvisioned ? (
                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-500" />
                  )}
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <code className="text-xs text-gray-500">{tenant.dns.appDomain}</code>
                    {tenant.dns.appProvisioned ? (
                      <CheckCircle className="h-3 w-3 text-emerald-500" />
                    ) : (
                      <XCircle className="h-3 w-3 text-red-500" />
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="text-xs text-gray-500">{tenant.dns.adminDomain}</code>
                    {tenant.dns.adminProvisioned ? (
                      <CheckCircle className="h-3 w-3 text-emerald-500" />
                    ) : (
                      <XCircle className="h-3 w-3 text-red-500" />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Plan Features */}
      {tenant.plan.features && Object.keys(tenant.plan.features).length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Recursos do Plano</h2>
          </div>
          <div className="p-6">
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {Object.entries(tenant.plan.features).map(([feature, enabled]) => (
                <div
                  key={feature}
                  className={`flex items-center gap-3 p-3 rounded-xl border ${
                    enabled ? 'border-emerald-100 bg-emerald-50/50' : 'border-gray-100 bg-gray-50/50'
                  }`}
                >
                  {enabled ? (
                    <CheckCircle className="h-5 w-5 text-emerald-500 shrink-0" />
                  ) : (
                    <XCircle className="h-5 w-5 text-gray-400 shrink-0" />
                  )}
                  <span className={`text-sm ${enabled ? 'text-emerald-700 font-medium' : 'text-gray-500'}`}>
                    {feature.replace(/([A-Z])/g, ' $1').trim()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Plan Limits */}
      {tenant.plan.limits && Object.keys(tenant.plan.limits).length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Limites do Plano</h2>
          </div>
          <div className="p-6">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {Object.entries(tenant.plan.limits).map(([limit, value]) => (
                <div key={limit} className="p-4 rounded-xl border border-gray-100 bg-gray-50/50">
                  <p className="text-xs text-gray-500 mb-1">{limit.replace(/([A-Z])/g, ' $1').trim()}</p>
                  <p className="text-2xl font-bold text-gray-900">{value.toLocaleString()}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
